import { ReactElement, useMemo } from 'react';

import { mapDataToUndefinedIfSkip } from 'modules/api/utils';
import { useConnection } from 'modules/auth/hooks/useConnection';
import { DelegateStakingRoutesConfig } from 'modules/delegateStaking/Routes';
import { useGlobalTranslation } from 'modules/i18n';
import { useGetAccountPoolsQuery } from 'modules/pool/actions/getAccountPools';
import {
  EMPTY_POOL_ADDRESSES,
  useGetPoolAddressesQuery,
} from 'modules/pool/actions/getPoolAddresses';

import { DelegateStakingTable } from '../DelegateStakingTable';
import { PendingDelegateStaking } from '../PendingDelegateStaking';
import { StakingSection } from '../StakingSection';
import { DelegateStakingEmpty } from './DelegateStakingEmpty';
import { translation } from './translation';

export function DelegateStaking(): ReactElement {
  const { keys, t } = useGlobalTranslation(translation);
  const { isConnected, connectionKey, cacheTime } = useConnection();

  const { data: pools = EMPTY_POOL_ADDRESSES } = useGetPoolAddressesQuery(
    undefined,
    { refetchOnMountOrArgChange: cacheTime },
  );

  const { data: accountPools } = useGetAccountPoolsQuery(
    { addresses: pools, connectionKey },
    {
      skip: !isConnected || !pools.length,
      selectFromResult: mapDataToUndefinedIfSkip,
      refetchOnMountOrArgChange: cacheTime,
    },
  );

  const accountPoolAddresses = useMemo(
    () => accountPools?.map(({ address }) => address) ?? [],
    [accountPools],
  );

  const isEmpty = !accountPoolAddresses?.length;

  return (
    <StakingSection
      isEmpty={isEmpty}
      title={t(keys.delegateStaking)}
      titleLink={DelegateStakingRoutesConfig.staking.generatePath()}
    >
      {isEmpty ? (
        <DelegateStakingEmpty />
      ) : (
        <DelegateStakingTable poolAddresses={accountPoolAddresses} />
      )}

      {isConnected && <PendingDelegateStaking pools={pools} />}
    </StakingSection>
  );
}
